import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './AdminPlayVaultConfig.css';

// ─── Conversion helpers ───────────────────────────────────────────────────────
const WEI = 1e18;
const toUsdt  = (wei)   => (Number(BigInt(wei)) / WEI).toString();
const toWei   = (usdt)  => BigInt(Math.round(parseFloat(usdt) * WEI)).toString();
const fromBps = (bps, factor, places = 2) => (bps / factor).toFixed(places);

// EV-based house edge for simple win/payout games
const houseEdgePct = (winBps, payoutBps) => {
  const ev = (winBps / 10000) * (payoutBps / 10000);
  return ((1 - ev) * 100).toFixed(1);
};

// Convert config (raw bps/wei) → form state (human-readable strings)
const configToForm = (c) => ({
  coinFlip: {
    winPct:   fromBps(c.coinFlip.winProbabilityBps, 100),
    payoutX:  fromBps(c.coinFlip.payoutMultiplierBps, 10000),
    minUsdt:  toUsdt(c.coinFlip.minBetWei),
    maxUsdt:  toUsdt(c.coinFlip.maxBetWei),
  },
  diceOverUnder: {
    winPct:   fromBps(c.diceOverUnder.winProbabilityBps, 100),
    payoutX:  fromBps(c.diceOverUnder.payoutMultiplierBps, 10000),
    minUsdt:  toUsdt(c.diceOverUnder.minBetWei),
    maxUsdt:  toUsdt(c.diceOverUnder.maxBetWei),
  },
  diceSeven: {
    winPct:   fromBps(c.diceSeven.winProbabilityBps, 100),
    payoutX:  fromBps(c.diceSeven.payoutMultiplierBps, 10000),
    minUsdt:  toUsdt(c.diceSeven.minBetWei),
    maxUsdt:  toUsdt(c.diceSeven.maxBetWei),
  },
  spinWheel: {
    segmentsX: (c.spinWheel.segmentsBps || []).map(b => (b / 10000).toFixed(2)).join(', '),
    minUsdt:   toUsdt(c.spinWheel.minBetWei),
    maxUsdt:   toUsdt(c.spinWheel.maxBetWei),
  },
  mines: {
    houseEdgePct: fromBps(c.mines.houseEdgeBps, 100),
    gridSize:     String(c.mines.gridSize),
    mineOptions:  (c.mines.mineOptions || []).join(', '),
    minUsdt:      toUsdt(c.mines.minBetWei),
    maxUsdt:      toUsdt(c.mines.maxBetWei),
  },
  crash: {
    maxTargetX: fromBps(c.crash.maxTargetMultiplierBps, 10000, 0),
    minUsdt:    toUsdt(c.crash.minBetWei),
    maxUsdt:    toUsdt(c.crash.maxBetWei),
  },
  plinko: {
    rows:      String(c.plinko.rows),
    bucketsX:  (c.plinko.bucketsBps || []).map(b => (b / 10000).toFixed(2)).join(', '),
    minUsdt:   toUsdt(c.plinko.minBetWei),
    maxUsdt:   toUsdt(c.plinko.maxBetWei),
  },
  keno: {
    poolSize:   String(c.keno.poolSize),
    drawCount:  String(c.keno.drawCount),
    pickCount:  String(c.keno.pickCount),
    payoutsX:   (c.keno.payoutTableBps || []).map(b => (b / 10000).toFixed(2)).join(', '),
    minUsdt:    toUsdt(c.keno.minBetWei),
    maxUsdt:    toUsdt(c.keno.maxBetWei),
  },
  roulette: {
    straightUpX:  fromBps(c.roulette.straightUpPayoutBps, 10000),
    redBlackX:    fromBps(c.roulette.redBlackPayoutBps, 10000),
    dozenColumnX: fromBps(c.roulette.dozenColumnPayoutBps, 10000),
    minUsdt:      toUsdt(c.roulette.minBetWei),
    maxUsdt:      toUsdt(c.roulette.maxBetWei),
  },
  blackjack: {
    blackjackPayoutX: fromBps(c.blackjack?.blackjackPayoutBps ?? 25000, 10000),
    winPayoutX:       fromBps(c.blackjack?.winPayoutBps       ?? 20000, 10000),
    minUsdt:          toUsdt(c.blackjack?.minBetWei  ?? '10000000000000000'),
    maxUsdt:          toUsdt(c.blackjack?.maxBetWei  ?? '100000000000000000000'),
  },
  videoPoker: {
    royalFlushX:    fromBps(c.videoPoker?.royalFlushBps    ?? 8000000, 10000, 0),
    straightFlushX: fromBps(c.videoPoker?.straightFlushBps ?? 500000,  10000, 0),
    fourOfAKindX:   fromBps(c.videoPoker?.fourOfAKindBps   ?? 250000,  10000, 0),
    fullHouseX:     fromBps(c.videoPoker?.fullHouseBps     ?? 80000,   10000, 0),
    flushX:         fromBps(c.videoPoker?.flushBps         ?? 50000,   10000, 0),
    straightX:      fromBps(c.videoPoker?.straightBps      ?? 40000,   10000, 0),
    threeOfAKindX:  fromBps(c.videoPoker?.threeOfAKindBps  ?? 30000,   10000, 0),
    twoPairX:       fromBps(c.videoPoker?.twoPairBps       ?? 20000,   10000, 0),
    jacksOrBetterX: fromBps(c.videoPoker?.jacksOrBetterBps ?? 10000,   10000, 0),
    minUsdt:        toUsdt(c.videoPoker?.minBetWei  ?? '10000000000000000'),
    maxUsdt:        toUsdt(c.videoPoker?.maxBetWei  ?? '10000000000000000000'),
  },
});

// Convert form state → raw bps/wei payload
const formToPayload = (f) => {
  const parseArr = (str) => str.split(',').map(s => Math.round(parseFloat(s.trim()) * 10000));
  const parseIntArr = (str) => str.split(',').map(s => parseInt(s.trim(), 10));

  return {
    coinFlip: {
      winProbabilityBps:   Math.round(parseFloat(f.coinFlip.winPct) * 100),
      payoutMultiplierBps: Math.round(parseFloat(f.coinFlip.payoutX) * 10000),
      minBetWei: toWei(f.coinFlip.minUsdt),
      maxBetWei: toWei(f.coinFlip.maxUsdt),
    },
    diceOverUnder: {
      winProbabilityBps:   Math.round(parseFloat(f.diceOverUnder.winPct) * 100),
      payoutMultiplierBps: Math.round(parseFloat(f.diceOverUnder.payoutX) * 10000),
      minBetWei: toWei(f.diceOverUnder.minUsdt),
      maxBetWei: toWei(f.diceOverUnder.maxUsdt),
    },
    diceSeven: {
      winProbabilityBps:   Math.round(parseFloat(f.diceSeven.winPct) * 100),
      payoutMultiplierBps: Math.round(parseFloat(f.diceSeven.payoutX) * 10000),
      minBetWei: toWei(f.diceSeven.minUsdt),
      maxBetWei: toWei(f.diceSeven.maxUsdt),
    },
    spinWheel: {
      segmentsBps: parseArr(f.spinWheel.segmentsX),
      minBetWei:   toWei(f.spinWheel.minUsdt),
      maxBetWei:   toWei(f.spinWheel.maxUsdt),
    },
    mines: {
      houseEdgeBps: Math.round(parseFloat(f.mines.houseEdgePct) * 100),
      gridSize:     parseInt(f.mines.gridSize, 10),
      mineOptions:  parseIntArr(f.mines.mineOptions),
      minBetWei:    toWei(f.mines.minUsdt),
      maxBetWei:    toWei(f.mines.maxUsdt),
    },
    crash: {
      maxTargetMultiplierBps: Math.round(parseFloat(f.crash.maxTargetX) * 10000),
      minBetWei: toWei(f.crash.minUsdt),
      maxBetWei: toWei(f.crash.maxUsdt),
    },
    plinko: {
      rows:       parseInt(f.plinko.rows, 10),
      bucketsBps: parseArr(f.plinko.bucketsX),
      minBetWei:  toWei(f.plinko.minUsdt),
      maxBetWei:  toWei(f.plinko.maxUsdt),
    },
    keno: {
      poolSize:      parseInt(f.keno.poolSize, 10),
      drawCount:     parseInt(f.keno.drawCount, 10),
      pickCount:     parseInt(f.keno.pickCount, 10),
      payoutTableBps: parseArr(f.keno.payoutsX),
      minBetWei:     toWei(f.keno.minUsdt),
      maxBetWei:     toWei(f.keno.maxUsdt),
    },
    roulette: {
      straightUpPayoutBps:  Math.round(parseFloat(f.roulette.straightUpX) * 10000),
      redBlackPayoutBps:    Math.round(parseFloat(f.roulette.redBlackX) * 10000),
      dozenColumnPayoutBps: Math.round(parseFloat(f.roulette.dozenColumnX) * 10000),
      minBetWei: toWei(f.roulette.minUsdt),
      maxBetWei: toWei(f.roulette.maxUsdt),
    },
    blackjack: {
      blackjackPayoutBps: Math.round(parseFloat(f.blackjack.blackjackPayoutX) * 10000),
      winPayoutBps:       Math.round(parseFloat(f.blackjack.winPayoutX) * 10000),
      minBetWei: toWei(f.blackjack.minUsdt),
      maxBetWei: toWei(f.blackjack.maxUsdt),
    },
    videoPoker: {
      royalFlushBps:    Math.round(parseFloat(f.videoPoker.royalFlushX) * 10000),
      straightFlushBps: Math.round(parseFloat(f.videoPoker.straightFlushX) * 10000),
      fourOfAKindBps:   Math.round(parseFloat(f.videoPoker.fourOfAKindX) * 10000),
      fullHouseBps:     Math.round(parseFloat(f.videoPoker.fullHouseX) * 10000),
      flushBps:         Math.round(parseFloat(f.videoPoker.flushX) * 10000),
      straightBps:      Math.round(parseFloat(f.videoPoker.straightX) * 10000),
      threeOfAKindBps:  Math.round(parseFloat(f.videoPoker.threeOfAKindX) * 10000),
      twoPairBps:       Math.round(parseFloat(f.videoPoker.twoPairX) * 10000),
      jacksOrBetterBps: Math.round(parseFloat(f.videoPoker.jacksOrBetterX) * 10000),
      minBetWei: toWei(f.videoPoker.minUsdt),
      maxBetWei: toWei(f.videoPoker.maxUsdt),
    },
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function Field({ label, value, onChange, type = 'text', hint, badge }) {
  return (
    <div className="pvc-field">
      <label className="pvc-label">
        {label}
        {badge && <span className="pvc-badge">{badge}</span>}
      </label>
      <input
        className="pvc-input"
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        step="any"
      />
      {hint && <span className="pvc-hint">{hint}</span>}
    </div>
  );
}

function Section({ title, icon, children, edge }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="pvc-section">
      <button className="pvc-section-header" onClick={() => setOpen(o => !o)}>
        <span>{icon} {title}</span>
        <span className="pvc-section-right">
          {edge && <span className="pvc-edge-badge">House edge: {edge}%</span>}
          <span className="pvc-chevron">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {open && <div className="pvc-section-body">{children}</div>}
    </div>
  );
}

function BetLimits({ form, game, set }) {
  return (
    <div className="pvc-bet-limits">
      <Field
        label="Min bet (USDT)"
        value={form[game].minUsdt}
        onChange={v => set(game, 'minUsdt', v)}
        type="number"
      />
      <Field
        label="Max bet (USDT)"
        value={form[game].maxUsdt}
        onChange={v => set(game, 'maxUsdt', v)}
        type="number"
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminPlayVaultConfig = () => {
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/game-config');
      setForm(configToForm(res.data.config));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load game config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (game, field, value) => {
    setForm(prev => ({ ...prev, [game]: { ...prev[game], [field]: value } }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = formToPayload(form);
      await api.put('/admin/game-config', payload);
      toast.success('Game config saved!');
    } catch (e) {
      const msg = e.response?.data?.message || 'Save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="pvc-loading">Loading game config…</div>;
  if (!form)   return <div className="pvc-error">{error || 'Could not load config'}</div>;

  // Live house edge calculations
  const cfEdge  = houseEdgePct(Math.round(parseFloat(form.coinFlip.winPct) * 100),       Math.round(parseFloat(form.coinFlip.payoutX) * 10000));
  const douEdge = houseEdgePct(Math.round(parseFloat(form.diceOverUnder.winPct) * 100),  Math.round(parseFloat(form.diceOverUnder.payoutX) * 10000));
  const d7Edge  = houseEdgePct(Math.round(parseFloat(form.diceSeven.winPct) * 100),      Math.round(parseFloat(form.diceSeven.payoutX) * 10000));

  const wheelSegsBps = form.spinWheel.segmentsX.split(',').map(s => Math.round(parseFloat(s.trim()) * 10000) || 0);
  const wheelEv = wheelSegsBps.reduce((a, b) => a + b, 0) / (wheelSegsBps.length * 10000);
  const wheelEdge = ((1 - wheelEv) * 100).toFixed(1);

  return (
    <div className="pvc-root">
      <div className="pvc-header">
        <h2>Game Config</h2>
        <p className="pvc-subtitle">
          All changes take effect immediately. House edge validation is enforced server-side — saves will fail if player EV ≥ 100%.
        </p>
      </div>

      {/* ── Coin Flip ─────────────────────────────── */}
      <Section title="Coin Flip" icon="🪙" edge={cfEdge}>
        <div className="pvc-row">
          <Field
            label="Win probability (%)"
            value={form.coinFlip.winPct}
            onChange={v => set('coinFlip', 'winPct', v)}
            type="number"
            hint="Recommended: 38–45%"
            badge={`${form.coinFlip.winPct}% win`}
          />
          <Field
            label="Payout multiplier (×)"
            value={form.coinFlip.payoutX}
            onChange={v => set('coinFlip', 'payoutX', v)}
            type="number"
            hint="Recommended: 1.9–2.0×"
          />
        </div>
        <BetLimits form={form} game="coinFlip" set={set} />
      </Section>

      {/* ── Dice Over/Under ───────────────────────── */}
      <Section title="Dice — Over / Under 7" icon="🎲" edge={douEdge}>
        <div className="pvc-row">
          <Field
            label="Win probability (%)"
            value={form.diceOverUnder.winPct}
            onChange={v => set('diceOverUnder', 'winPct', v)}
            type="number"
            hint="True P = 41.7% (5/12 outcomes). Recommended: 36–40%"
          />
          <Field
            label="Payout multiplier (×)"
            value={form.diceOverUnder.payoutX}
            onChange={v => set('diceOverUnder', 'payoutX', v)}
            type="number"
            hint="Recommended: 2.3–2.4×"
          />
        </div>
        <BetLimits form={form} game="diceOverUnder" set={set} />
      </Section>

      {/* ── Dice Seven ────────────────────────────── */}
      <Section title="Dice — Exactly 7" icon="🎲" edge={d7Edge}>
        <div className="pvc-row">
          <Field
            label="Win probability (%)"
            value={form.diceSeven.winPct}
            onChange={v => set('diceSeven', 'winPct', v)}
            type="number"
            hint="True P = 16.7% (6/36 outcomes). Recommended: 14–15%"
          />
          <Field
            label="Payout multiplier (×)"
            value={form.diceSeven.payoutX}
            onChange={v => set('diceSeven', 'payoutX', v)}
            type="number"
            hint="Recommended: 5.0–5.5×"
          />
        </div>
        <BetLimits form={form} game="diceSeven" set={set} />
      </Section>

      {/* ── Spin Wheel ────────────────────────────── */}
      <Section title="Spin Wheel" icon="🎡" edge={isNaN(wheelEdge) ? '—' : wheelEdge}>
        <div className="pvc-row pvc-row-wide">
          <Field
            label="Segment multipliers (×), comma-separated"
            value={form.spinWheel.segmentsX}
            onChange={v => set('spinWheel', 'segmentsX', v)}
            hint={`${wheelSegsBps.length} segments · EV = ${wheelEv.toFixed(3)} · Use 0 for a losing segment. Avg must be < 1×.`}
          />
        </div>
        <BetLimits form={form} game="spinWheel" set={set} />
      </Section>

      {/* ── Crash ─────────────────────────────────── */}
      <Section title="Crash" icon="🚀" edge="~1 (formula-based)">
        <div className="pvc-info-box">
          House edge is baked into the crash formula: crash point = 9900 / rand (in bps), giving ~1% edge. You can only change bet limits and the max allowed target.
        </div>
        <div className="pvc-row">
          <Field
            label="Max target multiplier (×)"
            value={form.crash.maxTargetX}
            onChange={v => set('crash', 'maxTargetX', v)}
            type="number"
            hint="Hard cap on what users can target. Default 1000×"
          />
        </div>
        <BetLimits form={form} game="crash" set={set} />
      </Section>

      {/* ── Plinko ────────────────────────────────── */}
      <Section title="Plinko" icon="🎯">
        <div className="pvc-info-box">
          House edge is determined by bucket values vs binomial distribution. The server rejects any config where EV ≥ 1. There must be exactly <strong>rows + 1</strong> bucket values.
        </div>
        <div className="pvc-row">
          <Field
            label="Rows"
            value={form.plinko.rows}
            onChange={v => set('plinko', 'rows', v)}
            type="number"
            hint="Number of peg rows. Default 8 → 9 buckets."
          />
        </div>
        <div className="pvc-row pvc-row-wide">
          <Field
            label="Bucket payouts (×), comma-separated left→right"
            value={form.plinko.bucketsX}
            onChange={v => set('plinko', 'bucketsX', v)}
            hint={`Must have exactly ${parseInt(form.plinko.rows, 10) + 1} values. Center bucket is the jackpot. Keep outer buckets < 1× for house edge.`}
          />
        </div>
        <BetLimits form={form} game="plinko" set={set} />
      </Section>

      {/* ── Mines ─────────────────────────────────── */}
      <Section title="Mines" icon="💣" edge={`${form.mines.houseEdgePct} (baked-in)`}>
        <div className="pvc-info-box">
          House edge is subtracted from the multiplier on every reveal. Higher % = lower payouts per tile. Removing small mine counts (like 1) forces higher risk.
        </div>
        <div className="pvc-row">
          <Field
            label="House edge (%)"
            value={form.mines.houseEdgePct}
            onChange={v => set('mines', 'houseEdgePct', v)}
            type="number"
            hint="Recommended: 5–10%. Was 3% (too low)."
          />
          <Field
            label="Grid size"
            value={form.mines.gridSize}
            onChange={v => set('mines', 'gridSize', v)}
            type="number"
            hint="Total tiles. Default 25 (5×5)."
          />
        </div>
        <div className="pvc-row pvc-row-wide">
          <Field
            label="Allowed mine counts (comma-separated)"
            value={form.mines.mineOptions}
            onChange={v => set('mines', 'mineOptions', v)}
            hint="Tip: Remove 1 to prevent easy wins. Recommended: 3, 5, 8, 12, 18"
          />
        </div>
        <BetLimits form={form} game="mines" set={set} />
      </Section>

      {/* ── Keno ──────────────────────────────────── */}
      <Section title="Keno" icon="🔢">
        <div className="pvc-info-box">
          Payout table is indexed by number of matches [0, 1, 2, … pickCount]. Must have exactly pickCount + 1 entries. Server validates EV using hypergeometric distribution.
        </div>
        <div className="pvc-row">
          <Field label="Pool size"  value={form.keno.poolSize}  onChange={v => set('keno', 'poolSize', v)}  type="number" hint="Numbers to pick from. Default 20." />
          <Field label="Draw count" value={form.keno.drawCount} onChange={v => set('keno', 'drawCount', v)} type="number" hint="Numbers drawn each round. Default 10." />
          <Field label="Pick count" value={form.keno.pickCount} onChange={v => set('keno', 'pickCount', v)} type="number" hint="Numbers player selects. Default 4." />
        </div>
        <div className="pvc-row pvc-row-wide">
          <Field
            label="Payout table (×), comma-separated [0 matches → max matches]"
            value={form.keno.payoutsX}
            onChange={v => set('keno', 'payoutsX', v)}
            hint={`Must have ${parseInt(form.keno.pickCount, 10) + 1} values. Example: 0, 0, 0.3, 2.0, 8.0`}
          />
        </div>
        <BetLimits form={form} game="keno" set={set} />
      </Section>

      {/* ── Roulette ──────────────────────────────── */}
      {form.roulette && (
        <Section title="Roulette" icon="🎰" edge="~2.7 (European, formula-based)">
          <div className="pvc-info-box">
            European single-zero roulette (37 pockets). House edge is fixed at ~2.7% (1/37).
            Payouts are validated server-side: straight-up EV = (1/37) × payout must be &lt; 1.
          </div>
          <div className="pvc-row">
            <Field
              label="Straight-up payout (×)"
              value={form.roulette.straightUpX}
              onChange={v => set('roulette', 'straightUpX', v)}
              type="number"
              hint="1 number. True P = 1/37. Max = 36.99×. Default 35×."
            />
            <Field
              label="Red / Black / Odd / Even payout (×)"
              value={form.roulette.redBlackX}
              onChange={v => set('roulette', 'redBlackX', v)}
              type="number"
              hint="18 pockets. True P = 18/37. Max = 2.055×. Default 1.94×."
            />
            <Field
              label="Dozen / Column payout (×)"
              value={form.roulette.dozenColumnX}
              onChange={v => set('roulette', 'dozenColumnX', v)}
              type="number"
              hint="12 pockets. True P = 12/37. Max = 3.083×. Default 2.94×."
            />
          </div>
          <BetLimits form={form} game="roulette" set={set} />
        </Section>
      )}

      {/* ── Blackjack ─────────────────────────────── */}
      {form.blackjack && (
        <Section title="Blackjack" icon="🃏" edge="~0.5–1 (player-dependent)">
          <div className="pvc-info-box">
            Standard rules: dealer hits on &lt;17, stands on 17+. Natural blackjack pays 1.5:1.
            Payouts are total return (bet + winnings). Win 2× = even money. BJ 2.5× = 1.5:1 on bet.
          </div>
          <div className="pvc-row">
            <Field
              label="Blackjack payout (×)"
              value={form.blackjack.blackjackPayoutX}
              onChange={v => set('blackjack', 'blackjackPayoutX', v)}
              type="number"
              hint="Natural blackjack total return. Default 2.5× (1.5:1 net). Max: keep below 3×."
            />
            <Field
              label="Normal win payout (×)"
              value={form.blackjack.winPayoutX}
              onChange={v => set('blackjack', 'winPayoutX', v)}
              type="number"
              hint="Regular win total return. Default 2× (even money). Keep at 2×."
            />
          </div>
          <BetLimits form={form} game="blackjack" set={set} />
        </Section>
      )}

      {/* ── Video Poker ───────────────────────────── */}
      {form.videoPoker && (
        <Section title="Video Poker" icon="🎴" edge="~2.7 (8/5 Jacks or Better)">
          <div className="pvc-info-box">
            5-card draw, Jacks or Better. Payouts are total return multipliers (×). Default is 8/5 pay table (~97.3% RTP / ~2.7% house edge).
          </div>
          <div className="pvc-row">
            <Field label="Royal Flush (×)"    value={form.videoPoker.royalFlushX}    onChange={v => set('videoPoker', 'royalFlushX', v)}    type="number" hint="Default 800×" />
            <Field label="Straight Flush (×)" value={form.videoPoker.straightFlushX} onChange={v => set('videoPoker', 'straightFlushX', v)} type="number" hint="Default 50×" />
            <Field label="Four of a Kind (×)" value={form.videoPoker.fourOfAKindX}   onChange={v => set('videoPoker', 'fourOfAKindX', v)}   type="number" hint="Default 25×" />
          </div>
          <div className="pvc-row">
            <Field label="Full House (×)"     value={form.videoPoker.fullHouseX}     onChange={v => set('videoPoker', 'fullHouseX', v)}     type="number" hint="Default 8×" />
            <Field label="Flush (×)"          value={form.videoPoker.flushX}         onChange={v => set('videoPoker', 'flushX', v)}         type="number" hint="Default 5×" />
            <Field label="Straight (×)"       value={form.videoPoker.straightX}      onChange={v => set('videoPoker', 'straightX', v)}      type="number" hint="Default 4×" />
          </div>
          <div className="pvc-row">
            <Field label="Three of a Kind (×)" value={form.videoPoker.threeOfAKindX} onChange={v => set('videoPoker', 'threeOfAKindX', v)} type="number" hint="Default 3×" />
            <Field label="Two Pair (×)"        value={form.videoPoker.twoPairX}      onChange={v => set('videoPoker', 'twoPairX', v)}      type="number" hint="Default 2×" />
            <Field label="Jacks or Better (×)" value={form.videoPoker.jacksOrBetterX} onChange={v => set('videoPoker', 'jacksOrBetterX', v)} type="number" hint="Default 1× (return bet)" />
          </div>
          <BetLimits form={form} game="videoPoker" set={set} />
        </Section>
      )}

      {/* ── Save ──────────────────────────────────── */}
      {error && <div className="pvc-error-box">{error}</div>}

      <div className="pvc-footer">
        <button className="pvc-reset-btn" onClick={load} disabled={saving}>
          ↺ Reset to saved
        </button>
        <button className="pvc-save-btn" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save All Changes'}
        </button>
      </div>
    </div>
  );
};

export default AdminPlayVaultConfig;
